import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  TablePagination
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Dashboard as DashboardIcon,
  AddLocation,
  CheckCircle,
  Schedule,
  Cancel,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import injupemplogo from './injupemplogo.png';

const ReporteriaPases = () => {
  // Estados para los datos
  const [pases, setPases] = useState([]);
  const [rutas, setRutas] = useState([]);
  const [areas, setAreas] = useState([]);
  const [conductores, setConductores] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  
  // Estados para filtros
  const [filtros, setFiltros] = useState({
    fechaInicio: '',
    fechaFin: '',
    estado: '',
    usuario: ''
  });
  
  // Estados para UI
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [pasesFiltrados, setPasesFiltrados] = useState([]);
  const [dialogFiltros, setDialogFiltros] = useState(false);

  // Estados para paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // URLs de las APIs (ajustar según tu entorno)
  const API_BASE_URL = 'https://bei360.injupemp.gob.hn:8444/api';
  const ENDPOINTS = {
    pases: `${API_BASE_URL}/pases/`,
    rutas: `${API_BASE_URL}/rutas/`,
    areas: `${API_BASE_URL}/areas/`,
    conductores: `${API_BASE_URL}/conductores/`,
    vehiculos: `${API_BASE_URL}/vehiculos/`
  };

  // Función para obtener datos de la API
  const fetchData = async (endpoint) => {
    try {
      const response = await fetch(endpoint);
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
      throw error;
    }
  };

  // Cargar todos los datos
  const cargarDatos = async () => {
    setLoading(true);
    setError('');
    
    try {
      const [
        pasesData,
        rutasData,
        areasData,
        conductoresData,
        vehiculosData
      ] = await Promise.all([
        fetchData(ENDPOINTS.pases),
        fetchData(ENDPOINTS.rutas),
        fetchData(ENDPOINTS.areas),
        fetchData(ENDPOINTS.conductores),
        fetchData(ENDPOINTS.vehiculos)
      ]);

      // ORDENAR POR ID DESCENDENTE (más simple y efectivo)
      const pasesOrdenados = pasesData.sort((a, b) => b.id - a.id);

      setPases(pasesOrdenados);
      setPasesFiltrados(pasesOrdenados);
      setRutas(rutasData);
      setAreas(areasData);
      setConductores(conductoresData);
      setVehiculos(vehiculosData);
    } catch (err) {
      setError('Error al cargar los datos. Verifique la conexión con el servidor.');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos al montar el componente
  useEffect(() => {
    cargarDatos();
  }, []);

  // Funciones auxiliares para obtener nombres
  const getNombreRuta = (rutaId) => {
    const ruta = rutas.find(r => r.id === rutaId);
    return ruta ? ruta.nombre : 'N/A';
  };

  const getNombreArea = (areaId) => {
    const area = areas.find(a => a.id === areaId);
    return area ? area.nombre : 'N/A';
  };

  const getNombreConductor = (conductorId) => {
    const conductor = conductores.find(c => c.id === conductorId);
    return conductor ? `${conductor.nombre} ${conductor.apellido}` : 'N/A';
  };

  const getInfoVehiculo = (vehiculoId) => {
    const vehiculo = vehiculos.find(v => v.id === vehiculoId);
    return vehiculo ? `${vehiculo.marca} ${vehiculo.modelo} ${vehiculo.año} ${vehiculo.placa} ${vehiculo.vin} ` : 'N/A';
  };

  // Formatear fecha
  const formatearFecha = (fechaISO) => {
    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtered = [...pases];

    if (filtros.fechaInicio) {
      filtered = filtered.filter(pase => {
        const fechaPase = new Date(pase.fecha_solicitud).toISOString().split('T')[0];
        return fechaPase >= filtros.fechaInicio;
      });
    }

    if (filtros.fechaFin) {
      filtered = filtered.filter(pase => {
        const fechaPase = new Date(pase.fecha_solicitud).toISOString().split('T')[0];
        return fechaPase <= filtros.fechaFin;
      });
    }

    if (filtros.estado) {
      filtered = filtered.filter(pase => pase.estado === filtros.estado);
    }

    if (filtros.usuario) {
      filtered = filtered.filter(pase => pase.usuario_id.toString() === filtros.usuario);
    }

    // Mantener el orden por ID descendente
    const filteredAndSorted = filtered.sort((a, b) => b.id - a.id);
    setPasesFiltrados(filteredAndSorted);
    setPage(0);
    setDialogFiltros(false);
  };

  // Limpiar filtros
  const limpiarFiltros = () => {
    setFiltros({
      fechaInicio: '',
      fechaFin: '',
      estado: '',
      usuario: ''
    });
    // Restaurar el orden original por ID descendente
    const pasesOrdenados = [...pases].sort((a, b) => b.id - a.id);
    setPasesFiltrados(pasesOrdenados);
    setPage(0);
    setDialogFiltros(false);
  };

  // Obtener usuarios únicos para el filtro
  const usuariosUnicos = [...new Set(pases.map(p => p.usuario_id))].map(usuarioId => {
    const usuario = pases.find(p => p.usuario_id === usuarioId);
    return {
      id: usuarioId,
      nombre: usuario ? usuario.usuario_nombre : 'Usuario desconocido'
    };
  });

  // Obtener estadísticas
  const estadisticas = {
    total: pasesFiltrados.length,
    completados: pasesFiltrados.filter(p => p.estado === 'completado').length,
    abiertos: pasesFiltrados.filter(p => p.estado === 'abierto').length,
    en_ruta: pasesFiltrados.filter(p => p.estado === 'en_ruta').length,
    anulados: pasesFiltrados.filter(p => p.estado === 'anulado').length
  };

  // Funciones de paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Obtener datos paginados
  const pasesPaginados = pasesFiltrados.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Función para convertir imagen a Base64
  const imageToBase64 = (imageUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      };
      img.onerror = reject;
      img.src = imageUrl;
    });
  };

 // Función de exportación corregida - FLUJO COMPLETAMENTE CONTINUO
  const exportarAExcel = async () => {
    setExporting(true);
    
    try {
      // Crear nuevo workbook
      const workbook = new ExcelJS.Workbook();
      
      // ===== HOJA ÚNICA CON FLUJO COMPLETAMENTE CONTINUO =====
      const worksheet = workbook.addWorksheet('Reporte de Pases INJUPEMP');

      let currentRow = 1;

      // 1. AGREGAR LOGO (sin afectar el flujo)
      try {
        const logoBase64 = await imageToBase64(injupemplogo);
        const base64Data = logoBase64.replace(/^data:image\/png;base64,/, '');
        
        const imageId = workbook.addImage({
          base64: base64Data,
          extension: 'png',
        });
        
        // Insertar logo en la posición B1:C2 (centrado)
        worksheet.addImage(imageId, {
          tl: { col: 1, row: 0 },  // Columna B, Fila 0
          br: { col: 3, row: 2 },  // Columna C, Fila 2
        });
        
        currentRow = 3; // Empezar después del logo
        
      } catch (logoError) {
        console.warn('No se pudo cargar el logo:', logoError);
        currentRow = 1;
      }

      // 2. TÍTULO PRINCIPAL (centrado en columnas B y C)
      const titleRow = worksheet.getRow(currentRow);
      titleRow.getCell(2).value = 'REPORTE DE PASES DE SALIDA - INJUPEMP'; // Columna B
      titleRow.getCell(2).font = { 
        bold: true, 
        size: 14, 
        color: { argb: 'FF00b6dd' } 
      };
      titleRow.height = 25;
      currentRow++;

      // 3. INFORMACIÓN BÁSICA DEL REPORTE (en columnas B y C)
      const infoData = [
        ['Fecha de generación:', new Date().toLocaleString('es-ES')],
        ['Total de registros:', pasesFiltrados.length],
        ['Período del reporte:', `${filtros.fechaInicio || 'Inicio'} - ${filtros.fechaFin || 'Fin'}`],
        ['Filtros aplicados:', `Estado: ${filtros.estado || 'Todos'}, Usuario: ${filtros.usuario ? usuariosUnicos.find(u => u.id.toString() === filtros.usuario)?.nombre : 'Todos'}`]
      ];

      infoData.forEach(([label, value]) => {
        const row = worksheet.getRow(currentRow);
        row.getCell(2).value = label;  // Columna B
        row.getCell(2).font = { bold: true };
        row.getCell(3).value = value;  // Columna C
        currentRow++;
      });

      currentRow++; // Espacio pequeño

      // 4. ESTADÍSTICAS (en columnas B y C)
      const statsTitleRow = worksheet.getRow(currentRow);
      statsTitleRow.getCell(2).value = 'ESTADÍSTICAS';  // Columna B
      statsTitleRow.getCell(2).font = { bold: true, color: { argb: 'FF00b6dd' } };
      currentRow++;

      const statsData = [
        ['Completados:', estadisticas.completados],
        ['Abiertos:', estadisticas.abiertos],
        ['En Ruta:', estadisticas.en_ruta],
        ['Anulados:', estadisticas.anulados],
        ['Tasa de completación:', estadisticas.total > 0 ? `${((estadisticas.completados / estadisticas.total) * 100).toFixed(1)}%` : '0%']
      ];

      statsData.forEach(([label, value]) => {
        const row = worksheet.getRow(currentRow);
        row.getCell(2).value = label;  // Columna B
        row.getCell(2).font = { bold: true };
        row.getCell(3).value = value;  // Columna C
        currentRow++;
      });

      currentRow++; // Espacio antes de la tabla

      // 5. ENCABEZADOS DE LA TABLA CON COLOR #00b6dd
      const encabezados = ['ID', 'Usuario', 'Motivo', 'Fecha Solicitud', 'Tipo Traslado', 'Estado', 'Ruta', 'Área', 'Conductor', 'Vehículo', 'Observaciones'];
      
      const headerRow = worksheet.getRow(currentRow);
      encabezados.forEach((header, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = header;
        cell.font = {
          bold: true,
          color: { argb: 'FFFFFFFF' },
          size: 10
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF00b6dd' }
        };
        cell.alignment = {
          horizontal: 'center',
          vertical: 'middle'
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        };
      });
      headerRow.height = 20;
      currentRow++;

      // 6. AGREGAR DATOS DE LA TABLA
      const datosExcel = pasesFiltrados.map(pase => ({
        'ID': pase.id,
        'Usuario': pase.usuario_nombre,
        'Motivo': pase.motivo,
        'Fecha Solicitud': new Date(pase.fecha_solicitud),
        'Tipo Traslado': pase.tipo_traslado,
        'Estado': pase.estado.toUpperCase(),
        'Ruta': getNombreRuta(pase.ruta),
        'Área': getNombreArea(pase.area),
        'Conductor': getNombreConductor(pase.conductor),
        'Vehículo': getInfoVehiculo(pase.vehiculo),
        'Observaciones': pase.observaciones_finales || 'Sin observaciones'
      }));

      if (datosExcel.length > 0) {
        datosExcel.forEach((pase) => {
          const row = worksheet.getRow(currentRow);
          
          Object.values(pase).forEach((value, colIndex) => {
            const cell = row.getCell(colIndex + 1);
            cell.value = value;
            
            // Formato especial para fechas
            if (value instanceof Date) {
              cell.numFmt = 'dd/mm/yyyy hh:mm';
            }
            
            // Bordes simples para todas las celdas
            cell.border = {
              top: { style: 'thin', color: { argb: 'FFD3D3D3' } },
              left: { style: 'thin', color: { argb: 'FFD3D3D3' } },
              bottom: { style: 'thin', color: { argb: 'FFD3D3D3' } },
              right: { style: 'thin', color: { argb: 'FFD3D3D3' } }
            };
          });
          
          currentRow++;
        });
      } else {
        // Si no hay datos
        const emptyRow = worksheet.getRow(currentRow);
        emptyRow.getCell(1).value = 'No hay datos disponibles para mostrar';
        emptyRow.getCell(1).font = { italic: true, color: { argb: 'FF666666' } };
        currentRow++;
      }

      // 7. CONFIGURACIÓN DE COLUMNAS (más compactas)
      const columnWidths = [8, 15, 25, 18, 12, 10, 12, 15, 18, 20, 25];
      columnWidths.forEach((width, index) => {
        worksheet.getColumn(index + 1).width = width;
      });

      // 8. AUTO FILTROS 
      if (datosExcel.length > 0) {
        const headerRowNumber = currentRow - datosExcel.length - 1;
        worksheet.autoFilter = {
          from: { row: headerRowNumber, column: 1 },
          to: { row: headerRowNumber + datosExcel.length, column: encabezados.length }
        };
      }

      // GENERAR ARCHIVO
      const fecha = new Date();
      const fechaStr = fecha.toISOString().split('T')[0];
      const nombreArchivo = `Reporte_Pases_INJUPEMP_${fechaStr}.xlsx`;

      // Guardar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      
      // Descargar archivo
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = nombreArchivo;
      link.click();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      setError('Error al exportar el reporte: ' + error.message);
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };
  // Componente de tarjeta de estadísticas
  const TarjetaEstadistica = ({ titulo, valor, icono, color }) => (
    <Card sx={{ 
      bgcolor: color, 
      color: 'white', 
      textAlign: 'center',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 4
      }
    }}>
      <CardContent>
        {icono}
        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', my: 1 }}>
          {valor}
        </Typography>
        <Typography variant="h6" component="div">
          {titulo}
        </Typography>
      </CardContent>
    </Card>
  );

  // Componente de chip de estado
  const ChipEstado = ({ estado }) => {
    const config = {
      completado: { color: 'success', icon: <CheckCircle /> },
      abierto: { color: 'warning', icon: <Schedule /> },
      anulado: { color: 'error', icon: <Cancel /> },
      en_ruta: { color: 'primary', icon: <AddLocation /> }
    }[estado] || { color: 'default', icon: null };

    return (
      <Chip
        label={estado.toUpperCase()}
        color={config.color}
        icon={config.icon}
        size="small"
        variant="filled"
      />
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Cargando datos...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Header */}
      <AppBar position="static" color="primary" elevation={2}>
        <Toolbar>
          <DashboardIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            Reportería de Pases
          </Typography>
          <Tooltip title="Recargar datos">
            <IconButton color="inherit" onClick={cargarDatos} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mt: 2 }} 
          onClose={() => setError('')}
          action={
            <Button color="inherit" size="small" onClick={cargarDatos}>
              Reintentar
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Estadísticas */}
      <Grid container spacing={3} sx={{ mt: 1, mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <TarjetaEstadistica
            titulo="Total de Pases"
            valor={estadisticas.total}
            icono={<DashboardIcon sx={{ fontSize: 40, opacity: 0.9 }} />}
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <TarjetaEstadistica
            titulo="Completados"
            valor={estadisticas.completados}
            icono={<CheckCircle sx={{ fontSize: 40, opacity: 0.9 }} />}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <TarjetaEstadistica
            titulo="En Ruta"
            valor={estadisticas.en_ruta}
            icono={<AddLocation sx={{ fontSize: 40, opacity: 0.9 }} />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <TarjetaEstadistica
            titulo="Abiertos"
            valor={estadisticas.abiertos}
            icono={<Schedule sx={{ fontSize: 40, opacity: 0.9 }} />}
            color="warning.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <TarjetaEstadistica
            titulo="Anulados"
            valor={estadisticas.anulados}
            icono={<Cancel sx={{ fontSize: 40, opacity: 0.9 }} />}
            color="error.main"
          />
        </Grid>
      </Grid>

      {/* Barra de acciones */}
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={8}>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                Resumen de Resultados
              </Typography>
              <Typography variant="body1" color="textSecondary">
                {pasesFiltrados.length} pases encontrados • 
                Completados: {estadisticas.completados} • 
                En ruta: {estadisticas.en_ruta} • 
                Abiertos: {estadisticas.abiertos} • 
                Anulados: {estadisticas.anulados}
                {estadisticas.total > 0 && (
                  <span> • Tasa de completación: {((estadisticas.completados / estadisticas.total) * 100).toFixed(1)}%</span>
                )}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setDialogFiltros(true)}
                disabled={pases.length === 0}
              >
                Filtros
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={exportarAExcel}
                disabled={exporting || pasesFiltrados.length === 0}
                sx={{ minWidth: 160 }}
              >
                {exporting ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Exportando...
                  </>
                ) : (
                  'Descargar Excel'
                )}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
        {exporting && <LinearProgress />}
      </Card>

      {/* Tabla de pases con paginación - ORDENADO POR ID DESCENDENTE */}
      <Card sx={{ boxShadow: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="medium">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    ID
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Usuario
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Motivo
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Fecha Solicitud
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Tipo Traslado
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Estado
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Ruta
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Área
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Conductor
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                    Vehículo
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pasesPaginados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                      <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                        <FilterIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                        <Typography variant="h6" gutterBottom>
                          No se encontraron pases
                        </Typography>
                        <Typography variant="body2">
                          {pases.length === 0 
                            ? 'No hay datos disponibles' 
                            : 'Intente ajustar los filtros de búsqueda'
                          }
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  pasesPaginados.map((pase) => (
                    <TableRow 
                      key={pase.id} 
                      hover 
                      sx={{ 
                        '&:last-child td, &:last-child th': { border: 0 },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <TableCell component="th" scope="row">
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                          #{pase.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {pase.usuario_nombre}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {pase.motivo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {formatearFecha(pase.fecha_solicitud)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={pase.tipo_traslado} 
                          size="small" 
                          variant="outlined"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <ChipEstado estado={pase.estado} />
                      </TableCell>
                      <TableCell>{getNombreRuta(pase.ruta)}</TableCell>
                      <TableCell>{getNombreArea(pase.area)}</TableCell>
                      <TableCell>{getNombreConductor(pase.conductor)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                          {getInfoVehiculo(pase.vehiculo)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Paginación */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={pasesFiltrados.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
            }
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              '.MuiTablePagination-toolbar': {
                minHeight: '60px'
              }
            }}
          />
        </CardContent>
      </Card>

      {/* Dialog de filtros */}
      <Dialog open={dialogFiltros} onClose={() => setDialogFiltros(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Filtros de Búsqueda Avanzados
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1, p: 1 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom color="primary">
                Rango de Fechas
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Fecha Inicio"
                    type="date"
                    fullWidth
                    value={filtros.fechaInicio}
                    onChange={(e) => setFiltros({ ...filtros, fechaInicio: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Fecha Fin"
                    type="date"
                    fullWidth
                    value={filtros.fechaFin}
                    onChange={(e) => setFiltros({ ...filtros, fechaFin: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom color="primary">
                Filtros Adicionales
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Estado"
                    select
                    fullWidth
                    value={filtros.estado}
                    onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}
                    size="small"
                  >
                    <MenuItem value="">Todos los estados</MenuItem>
                    <MenuItem value="completado">Completado</MenuItem>
                    <MenuItem value="en_ruta">En Ruta</MenuItem>
                    <MenuItem value="abierto">Abiertos</MenuItem>
                    <MenuItem value="anulado">Anulados</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Usuario"
                    select
                    fullWidth
                    value={filtros.usuario}
                    onChange={(e) => setFiltros({ ...filtros, usuario: e.target.value })}
                    size="small"
                  >
                    <MenuItem value="">Todos los usuarios</MenuItem>
                    {usuariosUnicos.map(usuario => (
                      <MenuItem key={usuario.id} value={usuario.id.toString()}>
                        {usuario.nombre}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={limpiarFiltros} 
            startIcon={<ClearIcon />}
            color="inherit"
          >
            Limpiar Filtros
          </Button>
          <Button 
            onClick={aplicarFiltros} 
            variant="contained" 
            sx={{ minWidth: 120 }}
          >
            Aplicar Filtros
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReporteriaPases;